import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';

const ToastContext = createContext(null);

const TOAST_STYLES = {
  success: {
    border: 'border-[#1f7a43]',
    icon: CheckCircle2,
    iconColor: 'text-[#4ADE80]',
    badge: 'SUCCESS',
  },
  error: {
    border: 'border-[#8B1E1E]',
    icon: AlertCircle,
    iconColor: 'text-[#F87171]',
    badge: 'ERROR',
  },
  info: {
    border: 'border-[#FF5200]',
    icon: Info,
    iconColor: 'text-[#FF5200]',
    badge: 'NOTICE',
  },
};

function ToastViewport({ toasts, dismissToast }) {
  return (
    <div className="pointer-events-none fixed inset-x-4 top-24 z-[120] flex flex-col gap-3 sm:right-6 sm:left-auto sm:w-full sm:max-w-sm">
      {toasts.map((toast) => {
        const style = TOAST_STYLES[toast.variant] || TOAST_STYLES.info;
        const Icon = style.icon;

        return (
          <div
            key={toast.id}
            className={`pointer-events-auto overflow-hidden border bg-[#0B0B0B]/95 shadow-[0_18px_40px_rgba(0,0,0,0.45)] backdrop-blur ${style.border}`}
          >
            <div className="flex items-start gap-3 p-4">
              <Icon className={`mt-0.5 shrink-0 ${style.iconColor}`} size={18} strokeWidth={1.75} />
              <div className="min-w-0 flex-1">
                <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gray-500">{style.badge}</p>
                <p className="mt-1 font-mono text-sm uppercase tracking-[0.12em] text-white">{toast.title}</p>
                {toast.description ? (
                  <p className="mt-2 text-sm leading-relaxed text-gray-300">{toast.description}</p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => dismissToast(toast.id)}
                className="rounded-none p-1 text-gray-500 transition-colors hover:text-white"
                aria-label="Dismiss notification"
              >
                <X size={16} strokeWidth={1.75} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismissToast = useCallback((id) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(({ title, description = '', variant = 'info', duration = 3200 }) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setToasts((current) => [...current, { id, title, description, variant }].slice(-4));
    window.setTimeout(() => {
      setToasts((current) => current.filter((toast) => toast.id !== id));
    }, duration);
    return id;
  }, []);

  const value = useMemo(() => ({
    showToast,
    dismissToast,
    success: (title, description = '', options = {}) => showToast({ title, description, variant: 'success', ...options }),
    error: (title, description = '', options = {}) => showToast({ title, description, variant: 'error', ...options }),
    info: (title, description = '', options = {}) => showToast({ title, description, variant: 'info', ...options }),
  }), [dismissToast, showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} dismissToast={dismissToast} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
